import { validateEmail } from "@baluu/ui/components/checkout/validation";

/**
 * Basket reservations (seat holds) during checkout, as both basket checkouts
 * take them: the widget's full-page checkout and the webcomponents overlay
 * Website v2 checks out through. Only for a school with
 * `basket.reservationsEnabled`.
 *
 * The lifecycle, server side (`BasketReservation.service.ts`):
 *   - `POST /widget/basket/reserve` soft-holds the basket's seats for the
 *     buyer's email for 5 minutes. It dedups by buyer + basket item, so
 *     calling it again only refreshes the rows it already holds.
 *   - `POST /widget/basket/validate` with `buyerEmail` is the heartbeat: it
 *     pushes the buyer's ACTIVE rows back to now + 5 minutes.
 *   - `createBasketCheckoutSession` reserves again right before Stripe (which
 *     covers holds that lapsed during a slow form-fill), attaches the
 *     PaymentIntent and stretches them to 10 minutes. Fulfilment marks them
 *     CONSUMED; a cron expires the rest.
 *   - `POST /widget/basket/release` frees them early: by item when one is
 *     removed, by buyer when checkout is left.
 *
 * No reservation id travels with the order: neither checkout sends one to
 * `createBasketCheckoutSession` or `confirmFreeBasketOrder` — the server finds
 * the holds by buyer email and basket item id. Neither shows a countdown
 * either: the heartbeat keeps the holds alive while checkout is open, and a
 * hold that lapsed (tab asleep) is re-taken at Pay, or refused there if the
 * seat went in the meantime.
 */

/** Wait this long after the email stops changing before reserving, so a
 *  half-typed address that already looks valid doesn't take its own holds. */
export const RESERVE_DEBOUNCE_MS = 500;

/** How often an open checkout re-validates with the buyer's email — the
 *  heartbeat that keeps the 5-minute holds alive. */
export const RESERVATION_HEARTBEAT_MS = 30_000;

/** `reason` values the release route records. */
export const RELEASE_REASON = {
  leftCheckout: "user-navigated-away",
  itemRemoved: "item-removed",
} as const;

/**
 * The email to hold seats under, or `null` when no holds should exist: the
 * school has reservations off, or the email is not yet a valid address.
 */
export function reservationEmail(
  email: string | null | undefined,
  reservationsEnabled: boolean,
): string | null {
  if (!reservationsEnabled) return null;
  const trimmed = (email ?? "").trim();
  if (!trimmed || !validateEmail(trimmed).isValid) return null;
  return trimmed;
}

/** One basket item the reserve route could not hold. */
export type ReserveConflict = {
  itemId: string;
  message?: string;
  unavailability?: unknown;
};

/** A per-item validate result, as `/widget/basket/validate` returns it. */
type ValidationResultLike = {
  itemId: string;
  available: boolean;
  message?: string;
  unavailability?: unknown;
};

export type ReserveOutcome<R extends ValidationResultLike> = {
  ok: boolean;
  reservationIds: string[];
  unavailableItems: ReserveConflict[];
  /** The per-item results to stamp on the basket, conflicts included;
   *  absent when the response carried none. */
  results?: R[];
};

/**
 * Stamp the reserve route's conflicts onto its per-item results.
 *
 * When the seats are free but someone else's hold takes them
 * (`ReservationConflictError`), the route answers 409 with the FIRST-pass
 * validation results — every item available — and the held-out items only in
 * `unavailableItems`. Applying `results` alone showed a basket with nothing
 * wrong until Pay refused it.
 */
export function withReserveConflicts<R extends ValidationResultLike>(
  results: R[],
  unavailableItems: ReserveConflict[],
): R[] {
  if (unavailableItems.length === 0) return results;
  const byId = new Map(unavailableItems.map((u) => [u.itemId, u]));
  return results.map((r) => {
    const conflict = byId.get(r.itemId);
    if (!conflict || !r.available) return r;
    // The server's own verdict shape, passed through as it came.
    return {
      ...r,
      available: false,
      message: conflict.message ?? "This item is no longer available",
      unavailability: conflict.unavailability ?? r.unavailability,
    } as R;
  });
}

/**
 * Read a `/widget/basket/reserve` response. A 409 is an answer (some items
 * could not be held), not a failure; any other non-2xx throws.
 */
export function parseReserveResponse<R extends ValidationResultLike>(
  status: number,
  data: unknown,
): ReserveOutcome<R> {
  const body = (data ?? {}) as {
    results?: R[];
    reservationIds?: string[];
    unavailableItems?: ReserveConflict[];
    error?: string;
  };
  if (status === 409) {
    const unavailableItems = body.unavailableItems ?? [];
    return {
      ok: false,
      reservationIds: [],
      unavailableItems,
      results: body.results
        ? withReserveConflicts(body.results, unavailableItems)
        : undefined,
    };
  }
  if (status < 200 || status >= 300) {
    throw new Error(body.error || `Reserve failed: ${status}`);
  }
  return {
    ok: true,
    reservationIds: body.reservationIds ?? [],
    unavailableItems: [],
    results: body.results,
  };
}

type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
};

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};

export type BasketHolds = {
  /**
   * The email holds should exist under now (`reservationEmail`), or `null`
   * for none. A new email is reserved after `debounceMs`; the previous one's
   * holds are released first. Repeating the current email does nothing.
   */
  update(email: string | null): void;
  /** Checkout is closing: cancel a pending reserve and release the holds. */
  dispose(): void;
  /**
   * The order is placed: stop without releasing. Fulfilment consumes the
   * holds asynchronously, so releasing them now would free the seats before
   * the booking takes them.
   */
  settle(): void;
  /** The email whose holds this checkout took, if any. */
  heldEmail(): string | null;
};

/**
 * One checkout's holds: reserve when a valid email settles, release what was
 * reserved when the email changes or checkout closes.
 *
 * Calls run one after another, so a release always lands after the reserve it
 * undoes and before any later reserve for the same email. Only an email that
 * was actually reserved is released, never every value the field passed
 * through while being typed.
 */
export function createBasketHolds({
  reserve,
  release,
  debounceMs = RESERVE_DEBOUNCE_MS,
  timers = defaultTimers,
}: {
  reserve: (email: string) => unknown;
  release: (email: string) => unknown;
  debounceMs?: number;
  timers?: Timers;
}): BasketHolds {
  let held: string | null = null;
  let scheduledFor: string | null = null;
  let timer: unknown = null;
  let done = false;
  let queue: Promise<unknown> = Promise.resolve();

  const enqueue = (op: () => unknown) => {
    const run = () => {
      try {
        return op();
      } catch {
        return undefined;
      }
    };
    queue = queue.then(run, run);
  };

  const cancelPending = () => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
    scheduledFor = null;
  };

  const releaseHeld = () => {
    const email = held;
    held = null;
    if (email) enqueue(() => release(email));
  };

  return {
    update(email) {
      if (done) return;
      if (email && (email === held || email === scheduledFor)) return;
      cancelPending();
      if (held !== email) releaseHeld();
      if (!email) return;
      scheduledFor = email;
      timer = timers.setTimeout(() => {
        timer = null;
        scheduledFor = null;
        if (done) return;
        held = email;
        enqueue(() => reserve(email));
      }, debounceMs);
    },
    dispose() {
      if (done) return;
      done = true;
      cancelPending();
      releaseHeld();
    },
    settle() {
      done = true;
      cancelPending();
      held = null;
    },
    heldEmail: () => held,
  };
}

type VisibilityTarget = {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", fn: () => void): void;
  removeEventListener(type: "visibilitychange", fn: () => void): void;
};

/**
 * Run the heartbeat every `intervalMs` while the page is visible. It stops
 * while the tab is hidden and, on return, beats at once and resumes. Returns
 * the stop function. `tick` decides for itself whether a beat is due (not
 * mid-submit, not after success).
 */
export function startReservationHeartbeat({
  tick,
  intervalMs = RESERVATION_HEARTBEAT_MS,
  doc = typeof document !== "undefined" ? document : undefined,
}: {
  tick: () => void;
  intervalMs?: number;
  doc?: VisibilityTarget;
}): () => void {
  let interval: ReturnType<typeof setInterval> | null = null;
  const beat = () => {
    if (doc?.hidden) return;
    tick();
  };
  const start = () => {
    if (interval) return;
    interval = setInterval(beat, intervalMs);
  };
  const stop = () => {
    if (!interval) return;
    clearInterval(interval);
    interval = null;
  };
  const onVisibility = () => {
    if (doc?.hidden) {
      stop();
    } else {
      beat();
      start();
    }
  };
  start();
  doc?.addEventListener("visibilitychange", onVisibility);
  return () => {
    stop();
    doc?.removeEventListener("visibilitychange", onVisibility);
  };
}
